import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TasksAPI } from "./tasks.api";
import {
  CreateTaskBody,
  CreateTaskResponse,
  DeleteTaskPathParams,
  DeleteTaskResponse,
  GetTasksInput,
  GetTasksResponse,
  UpdateTaskInput,
  UpdateTaskResponse,
} from "./tasks.types";

export const TasksQueryKeys = {
  all: ["tasks"] as const,
  lists: () => ["tasks", "list"] as const,
  list: (query?: GetTasksInput) => ["tasks", "list", query ?? {}] as const,
  details: () => ["tasks", "detail"] as const,
  detail: (uuid: string, query?: Omit<GetTasksInput, "uuid">) =>
    ["tasks", "detail", uuid, query ?? {}] as const,
};

export const useGetTasks = (query?: GetTasksInput) => {
  return useQuery<GetTasksResponse>({
    queryKey: TasksQueryKeys.list(query),
    queryFn: () => TasksAPI.get(query),
  });
};

export const useGetTask = (
  uuid: string,
  query?: Omit<GetTasksInput, "uuid">
) => {
  return useQuery<GetTasksResponse>({
    queryKey: TasksQueryKeys.detail(uuid, query),
    queryFn: () => TasksAPI.get({ ...query, uuid }),
    enabled: Boolean(uuid),
  });
};

export const useCreateTask = (options?: {
  onSuccess?: (data: CreateTaskResponse) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<CreateTaskResponse, unknown, CreateTaskBody>({
    mutationFn: TasksAPI.create,
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TasksQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: TasksQueryKeys.details() });
    },
  });
};

export const useUpdateTask = (options?: {
  onSuccess?: (data: UpdateTaskResponse) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<UpdateTaskResponse, unknown, UpdateTaskInput>({
    mutationFn: TasksAPI.update,
    onSuccess: (data, variables) => {
      options?.onSuccess?.(data);
      queryClient.invalidateQueries({
        queryKey: TasksQueryKeys.detail(variables.uuid),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TasksQueryKeys.lists() });
    },
  });
};

export const useDeleteTask = (options?: {
  onSuccess?: (data: DeleteTaskResponse) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<DeleteTaskResponse, unknown, DeleteTaskPathParams>({
    mutationFn: TasksAPI.delete,
    onSuccess: (data, variables) => {
      options?.onSuccess?.(data);
      queryClient.invalidateQueries({
        queryKey: TasksQueryKeys.detail(variables.uuid),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TasksQueryKeys.lists() });
    },
  });
};

export const TasksQuery = {
  queryKeys: TasksQueryKeys,
  useGetTasks,
  useGetTask,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
};
